import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  ArrowRight,
  Clock,
  Workflow,
  CheckCircle2,
  Sparkles,
  Lightbulb,
  Target,
  Zap,
  Database,
  Brain,
  Rocket,
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Timeline data
const timelineData = [
  {
    year: "2019",
    title: "Identifier le potentiel réel de l'IA, avant l'engouement",
    icon: Lightbulb,
    color: "#fad24b",
    content: [
      "Nous avons commencé à travailler sur les modèles de langage en **2019**, à la sortie de **GPT-2**, à une époque où l'IA générative n'était encore qu'un sujet de recherche pour la majorité des entreprises.",
      "À ce moment-là, je dirigeais **Businesscoot**, un spécialiste des études sectorielles. Une équipe de **15 analystes** produisait des études destinées à des cabinets de conseil, des banques, des cabinets d'avocats, des dirigeants de PME et des entreprises du CAC 40.",
    ],
    highlights: [
      "Lecture massive de sources",
      "Structuration d'informations hétérogènes",
      "Rédaction, synthèse, validation",
      "Tâches répétitives à faible valeur ajoutée",
    ],
    question: "Quelles parties de notre travail pouvaient être confiées à des modèles, sans dégrader la qualité de l'analyse ?",
    actions: [
      "Fine-tuning d'un premier modèle GPT-2",
      "Génération de premiers jets d'études",
      "Catégorisation automatique des sources",
      "Proposition de titres et synthèses dans notre backoffice Symfony",
    ],
    result: "Un analyste pouvait traiter jusqu'à 10 fois plus de sources qu'auparavant.",
  },
  {
    year: "2020",
    title: "Apprendre à déléguer à l'IA ce qui n'a pas de valeur analytique",
    icon: Target,
    color: "#3dd3ee",
    content: [
      "Un autre point bloquant est rapidement apparu : la rédaction des **profils d'entreprises** citées dans les études. C'était une tâche indispensable, mais chronophage et peu différenciante.",
    ],
    actions: [
      "Scraping de sources fiables (sites d'entreprises, LinkedIn, bases externes)",
      "Vérification par des règles métier",
      "Génération de profils respectant nos exigences éditoriales",
      "Suggestion des sources primaires utilisées pour la traçabilité",
    ],
    lesson: "Confier à l'IA les tâches à faible valeur ajoutée pour préserver l'expertise humaine là où elle est réellement utile.",
    note: "Par exemple, l'estimation de la taille d'un marché de niche restait — et reste encore — une compétence humaine : les IA y sont trop instables.",
  },
  {
    year: "2021",
    title: "SectorGPT : la première IA dédiée à l'analyse sectorielle",
    icon: Brain,
    color: "#b4dc19",
    content: [
      "Une évidence s'est alors imposée : **l'avenir de l'information sectorielle ne passerait plus par des PDF, mais par des interfaces conversationnelles.**",
      "En 2021, nous avons créé **SectorGPT** (rebaptisé ensuite **SectorAI**, suite à des échanges avec OpenAI).",
    ],
    idea: "Permettre à un utilisateur de poser une question, plutôt que de chercher une information dans des documents.",
    actions: [
      "Vectorisation de 1 500 études sectorielles",
      "Intégration de plus de 5 000 fiches entreprises",
      "Ajout de dizaines de milliers de sources validées",
      "Interface inspirée de ChatGPT, connectée uniquement à des données maîtrisées",
    ],
    insight: "C'est aussi à cette période que nous avons pris conscience des limites structurelles des LLM généralistes.",
  },
  {
    year: "2022–2023",
    title: "La source, le véritable graal de l'analyse",
    icon: Database,
    color: "#c8aff0",
    content: [
      "Les premiers usages de GPT-3 ont mis en lumière deux problèmes majeurs : les **hallucinations** et la **qualité incertaine des sources**.",
      "Pour aller plus loin, nous avons intégré **Indexpresse**, une base d'information sectorielle française couvrant plus de **150 revues spécialisées** et plus de **150 000 articles indexés**.",
    ],
    projects: [
      {
        title: "Construire le RAG sectoriel le plus complet possible",
        description: "Un RAG fondé sur des sources professionnelles, vérifiées et traçables.",
      },
      {
        title: "Repenser les workflows de traitement documentaire",
        description: "Indexpresse traite plusieurs centaines d'articles par jour. Nouveau workflow hybride : IA pour la sélection, classification et extraction ; humain pour le contrôle, validation et arbitrage.",
      },
    ],
    result: "Productivité multipliée par 3, sans perte de qualité.",
  },
  {
    year: "2024–2025",
    title: "Mettre ces compétences au service d'autres entreprises",
    icon: Rocket,
    color: "#ff8c19",
    content: [
      "Assez naturellement, d'autres entreprises sont venues à nous avec des problématiques similaires.",
      "L'un des cas les plus emblématiques concerne une entreprise industrielle dans la **manutention**, confrontée à des milliers de documents techniques, des centaines de paramètres produits et une forte complexité concurrentielle.",
    ],
    challenge: "Un commercial maîtrise vite son 80/20, mais il lui est impossible de connaître les configurations spécifiques, les évolutions techniques récentes, les différences fines avec les produits concurrents.",
    solution: [
      "Répondre sur la connaissance technique de centaines de matériels",
      "Identifier des cas clients pertinents",
      "Comparer les produits concurrents",
      "Générer un argumentaire commercial contextualisé",
    ],
    example: "Pour un prospect logistique, sensible aux questions de sécurité et de batteries, que mettre en avant face au concurrent ABC ?",
    implementation: [
      "Audit des besoins utilisateurs",
      "Structuration de plusieurs milliers de documents techniques",
      "Gestion de tableaux complexes multi-pages",
      "Plus de 30 heures d'interviews d'experts internes",
      "Intégration clé en main (front, back, accès via Microsoft Teams)",
      "Formation des équipes",
    ],
  },
];

// Timeline Item Component
const TimelineItem = ({ item, index }) => {
  const isEven = index % 2 === 0;
  
  const renderText = (text) => {
    if (!text) return null;
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, i) => 
      i % 2 === 1 ? <strong key={i} className="text-[#1e1919]">{part}</strong> : part
    );
  };

  return (
    <div className={`relative flex items-start ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-4 md:gap-12`}>
      {/* Timeline line and dot */}
      <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#61525a]/20 via-[#61525a]/10 to-transparent -translate-x-1/2 hidden md:block" />
      
      {/* Year badge - center on desktop */}
      <div className="absolute left-6 md:left-1/2 -translate-x-1/2 z-10">
        <div 
          className="w-10 h-10 md:w-14 md:h-14 rounded-full flex items-center justify-center shadow-lg border-4 border-white"
          style={{ background: `linear-gradient(135deg, ${item.color}, ${item.color}dd)` }}
        >
          <item.icon className="w-4 h-4 md:w-6 md:h-6 text-white" />
        </div>
      </div>
      
      {/* Content card */}
      <div className={`w-full md:w-[calc(50%-3rem)] ${isEven ? 'md:pr-4' : 'md:pl-4'} pl-16 md:pl-0`}>
        <div 
          className="bg-white rounded-xl p-5 md:p-6 shadow-md hover:shadow-lg transition-all duration-300 border-l-4"
          style={{ borderColor: item.color }}
        >
          {/* Year and Title */}
          <div className="mb-3">
            <span 
              className="inline-block px-2 py-0.5 rounded-full text-xs font-bold mb-2"
              style={{ background: `${item.color}20`, color: item.color }}
            >
              {item.year}
            </span>
            <h3 className="text-lg md:text-xl font-bold text-[#1e1919] leading-tight">{item.title}</h3>
          </div>
          
          {/* Content paragraphs */}
          {item.content && item.content.map((paragraph, i) => (
            <p key={i} className="text-[#736c64] leading-relaxed mb-3 text-sm">
              {renderText(paragraph)}
            </p>
          ))}
          
          {/* Highlights list */}
          {item.highlights && (
            <div className="mb-3">
              <p className="text-[#736c64] mb-1 text-sm">Notre quotidien était fait de :</p>
              <ul className="space-y-0.5">
                {item.highlights.map((h, i) => (
                  <li key={i} className="flex items-center gap-2 text-[#736c64] text-sm">
                    <span className="w-1 h-1 rounded-full" style={{ background: item.color }} />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Question */}
          {item.question && (
            <div className="bg-[#f7f5f2] rounded-lg p-3 mb-3 border-l-2" style={{ borderColor: item.color }}>
              <p className="text-[#1e1919] font-medium italic text-sm">{item.question}</p>
            </div>
          )}
          
          {/* Idea */}
          {item.idea && (
            <div className="bg-[#f7f5f2] rounded-lg p-3 mb-3 border-l-2" style={{ borderColor: item.color }}>
              <p className="text-[#1e1919] font-medium text-sm">{item.idea}</p>
            </div>
          )}
          
          {/* Challenge */}
          {item.challenge && (
            <div className="bg-[#f7f5f2] rounded-lg p-3 mb-3">
              <p className="text-[#736c64] text-sm">{item.challenge}</p>
            </div>
          )}
          
          {/* Actions */}
          {item.actions && (
            <div className="mb-3">
              <div className="space-y-1">
                {item.actions.map((action, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: item.color }} />
                    <span className="text-[#736c64] text-xs">{action}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Solution */}
          {item.solution && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-[#1e1919] mb-1">Assistant IA métier capable de :</p>
              <div className="space-y-1">
                {item.solution.map((s, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: item.color }} />
                    <span className="text-[#736c64] text-xs">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Example */}
          {item.example && (
            <div className="bg-[#1e1919] rounded-lg p-3 mb-3">
              <p className="text-xs text-[#bbb5ae] mb-0.5">Exemple réel :</p>
              <p className="text-white italic text-xs">"{item.example}"</p>
            </div>
          )}
          
          {/* Implementation */}
          {item.implementation && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-[#1e1919] mb-1">Le projet a inclus :</p>
              <div className="grid grid-cols-2 gap-1">
                {item.implementation.map((impl, i) => (
                  <div key={i} className="flex items-start gap-1">
                    <span className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: item.color }} />
                    <span className="text-[#736c64] text-xs">{impl}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Projects */}
          {item.projects && (
            <div className="space-y-2 mb-3">
              {item.projects.map((project, i) => (
                <div key={i} className="bg-[#f7f5f2] rounded-lg p-3">
                  <h4 className="font-semibold text-[#1e1919] text-xs mb-0.5">{i + 1}. {project.title}</h4>
                  <p className="text-[#736c64] text-xs">{project.description}</p>
                </div>
              ))}
            </div>
          )}
          
          {/* Lesson */}
          {item.lesson && (
            <div className="flex items-start gap-2 bg-[#f7f5f2] rounded-lg p-3 mb-3">
              <Zap className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: item.color }} />
              <p className="text-[#1e1919] font-medium text-xs">{item.lesson}</p>
            </div>
          )}
          
          {/* Note */}
          {item.note && (
            <p className="text-[#736c64] text-xs italic">{item.note}</p>
          )}
          
          {/* Insight */}
          {item.insight && (
            <p className="text-[#736c64] text-xs mt-2">👉 {item.insight}</p>
          )}
          
          {/* Result */}
          {item.result && (
            <div 
              className="mt-3 p-3 rounded-lg flex items-center gap-2"
              style={{ background: `${item.color}15` }}
            >
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: item.color }}
              >
                <Rocket className="w-4 h-4 text-white" />
              </div>
              <p className="font-semibold text-sm" style={{ color: item.color }}>
                👉 Résultat : {item.result}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Spacer for alternating layout */}
      <div className="hidden md:block w-[calc(50%-3rem)]" />
    </div>
  );
};

const HistoryPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const handleContactClick = () => {
    navigate('/');
    setTimeout(() => {
      const contactSection = document.querySelector('#contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-28 pb-12 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#3dd3ee]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#fad24b]/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
          <div
            className={`text-center transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm font-medium text-[#61525a] mb-6 shadow-sm">
              <Clock className="w-4 h-4" />
              Depuis 2019
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-[#1e1919] mb-4">
              Notre <span className="text-[#3dd3ee]">histoire</span>
            </h1>
            <p className="text-lg text-[#736c64] max-w-3xl mx-auto">
              Comment nous avons appris à faire de l'IA utile — <strong className="text-[#1e1919]">avant que ce soit à la mode</strong>
            </p>
          </div>
        </div>
      </section>
      
      {/* Timeline */}
      <section className="py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="relative space-y-8 md:space-y-16">
            {timelineData.map((item, index) => (
              <TimelineItem 
                key={item.year} 
                item={item} 
                index={index}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* Conclusion */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#1e1919] rounded-2xl p-6 md:p-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#3dd3ee]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#fad24b]/10 rounded-full blur-3xl" />
            
            <div className="relative">
              <h3 className="text-2xl font-bold text-white mb-4">
                Ce que nous proposons aujourd'hui
              </h3>
              
              <p className="text-[#bbb5ae] leading-relaxed mb-4 text-sm">
                Ce que nous proposons à nos clients, <strong className="text-white">nous l'avons d'abord fait pour nous-mêmes</strong>.
              </p>
              
              <div className="grid md:grid-cols-2 gap-3 mb-6">
                <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 rounded-lg bg-[#3dd3ee]/20 flex items-center justify-center">
                      <Database className="w-3.5 h-3.5 text-[#3dd3ee]" />
                    </div>
                    <span className="font-semibold text-white text-sm">RAG & bases documentaires</span>
                  </div>
                  <p className="text-xs text-[#bbb5ae]">Exploitation de bases documentaires complexes</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 rounded-lg bg-[#b4dc19]/20 flex items-center justify-center">
                      <Workflow className="w-3.5 h-3.5 text-[#b4dc19]" />
                    </div>
                    <span className="font-semibold text-white text-sm">Redesign de workflows</span>
                  </div>
                  <p className="text-xs text-[#bbb5ae]">Gains de productivité réelle</p>
                </div>
              </div>
              
              <p className="text-[#bbb5ae] leading-relaxed mb-4 text-sm">
                Si nous nous différencions, c'est parce que notre métier — <strong className="text-[#fad24b]">la donnée, l'analyse sectorielle, le traitement de l'information</strong> — a été l'un des premiers à être profondément impacté par l'IA.
              </p>
              
              <p className="text-[#bbb5ae] leading-relaxed mb-4 text-sm">
                Nous n'avons pas observé la transformation de loin. Nous l'avons <strong className="text-white">subie, expérimentée, corrigée — et maîtrisée</strong>.
              </p>
              
              <div className="bg-gradient-to-r from-[#3dd3ee]/20 to-[#b4dc19]/20 rounded-lg p-4 border border-white/10">
                <p className="text-white text-sm font-medium text-center">
                  Aujourd'hui, nous mettons ces enseignements au service d'entreprises qui veulent <strong className="text-[#3dd3ee]">aller au-delà du POC</strong>, et déployer une IA <strong className="text-[#b4dc19]">utile, fiable et adoptée</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-[#61525a]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <Sparkles className="w-10 h-10 text-[#fad24b] mx-auto mb-4" />
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Prêt à transformer votre entreprise avec l'IA ?
          </h2>
          <p className="text-white/80 mb-6">
            Découvrez comment nous pouvons vous accompagner.
          </p>
          <Button
            onClick={handleContactClick}
            className="bg-[#3dd3ee] hover:bg-[#2bc4df] text-white px-8 py-5 text-lg transition-all duration-300 hover:scale-105"
          >
            Nous contacter
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HistoryPage;
